import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { authorizedFetch } from '@nimbitlabs/ui';

export interface Notification {
  id: string;
  userId: string;
  type: 'alert' | 'report' | 'system';
  title: string;
  message: string;
  severity: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  actionUrl?: string;
  actionText?: string;
  createdAt: string;
}

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  
  fetchNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  sendNotification: (notification: {
    userId: string;
    type: string;
    title: string;
    message: string;
    severity: string;
    actionUrl?: string;
    actionText?: string;
  }) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

/**
 * Notifications Store
 * 
 * Dependencies: Auth context (user must be logged in)
 * 
 * This store manages user notifications with auto-polling.
 * Set up polling in your app with:
 * 
 * ```typescript
 * useEffect(() => {
 *   if (user) {
 *     const interval = setInterval(() => {
 *       useNotificationsStore.getState().fetchNotifications();
 *     }, 30000); // 30 seconds
 *     return () => clearInterval(interval);
 *   }
 * }, [user]);
 * ```
 */
export const useNotificationsStore = create<NotificationsState>()(
  subscribeWithSelector((set, get) => ({
    notifications: [],
    unreadCount: 0,
    loading: false,
    error: null,
    
    fetchNotifications: async () => {
      set({ loading: true, error: null });
      try {
        const response = await authorizedFetch('/notifications');
        if (!response.ok) throw new Error('Failed to fetch notifications');
        const notifications = await response.json() as Notification[];
        const unreadCount = notifications.filter(n => !n.read).length;
        set({ notifications, unreadCount, loading: false });
      } catch (e: any) {
        set({ error: e?.message ?? 'Failed to fetch notifications', loading: false });
      }
    },
    
    markAsRead: async (notificationId: string) => {
      try {
        const response = await authorizedFetch(`/notifications/${notificationId}/read`, {
          method: 'PATCH',
        });
        if (!response.ok) throw new Error('Failed to mark notification as read');
        
        // Update local state
        set(state => ({
          notifications: state.notifications.map(n =>
            n.id === notificationId ? { ...n, read: true } : n
          ),
          unreadCount: state.notifications.filter(n => 
            n.id === notificationId ? false : !n.read
          ).length,
        }));
      } catch (e: any) {
        set({ error: e?.message ?? 'Failed to mark notification as read' });
        throw e;
      }
    },
    
    markAllAsRead: async () => {
      try {
        const response = await authorizedFetch('/notifications/read-all', {
          method: 'PATCH',
        });
        if (!response.ok) throw new Error('Failed to mark all notifications as read');
        
        // Update local state
        set(state => ({
          notifications: state.notifications.map(n => ({ ...n, read: true })),
          unreadCount: 0,
        }));
      } catch (e: any) {
        set({ error: e?.message ?? 'Failed to mark all notifications as read' });
        throw e;
      }
    },
    
    deleteNotification: async (notificationId: string) => {
      try {
        const response = await authorizedFetch(`/notifications/${notificationId}`, {
          method: 'DELETE',
        });
        if (!response.ok) throw new Error('Failed to delete notification');
        
        // Update local state
        set(state => ({
          notifications: state.notifications.filter(n => n.id !== notificationId),
          unreadCount: state.notifications.filter(n => 
            n.id !== notificationId && !n.read
          ).length,
        }));
      } catch (e: any) {
        set({ error: e?.message ?? 'Failed to delete notification' });
        throw e;
      }
    },
    
    sendNotification: async (notification) => {
      try {
        const response = await authorizedFetch('/notifications', {
          method: 'POST',
          body: JSON.stringify(notification),
        });
        if (!response.ok) throw new Error('Failed to send notification');
        
        // Refresh notifications after sending
        await get().fetchNotifications();
      } catch (e: any) {
        set({ error: e?.message ?? 'Failed to send notification' });
        throw e;
      }
    },
    
    clearError: () => set({ error: null }),
    
    reset: () => set({ notifications: [], unreadCount: 0, loading: false, error: null }),
  }))
);

// Hook to use notifications
export function useNotifications() {
  const notifications = useNotificationsStore((state) => state.notifications);
  const unreadCount = useNotificationsStore((state) => state.unreadCount);
  const loading = useNotificationsStore((state) => state.loading);
  const fetchNotifications = useNotificationsStore((state) => state.fetchNotifications);
  const markAsRead = useNotificationsStore((state) => state.markAsRead);
  const markAllAsRead = useNotificationsStore((state) => state.markAllAsRead);
  const deleteNotification = useNotificationsStore((state) => state.deleteNotification);
  
  return {
    notifications,
    unreadCount,
    isLoading: loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };
}

// Note: Notifications are user-level, not account-dependent
// Set up auto-polling in your app based on user authentication
